import { isFinite } from '@bluebrick/saas-ui'
import { type Duration, type DateTime } from 'luxon'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'
import { dateTimeFormattedShort } from '@/common/dateUtils'
import { getEventEndDate, getDuration } from '@/common/utils'
import { useEventDetailsBaseStore } from '@/features/eventDetails/useEventDetailsBaseStore'
import { useEventDetailsRemoteDataStore } from '@/features/eventDetails/useEventDetailsRemoteDataStore'

export function useEventDetailsCooldown() {
	const { endDate, sourceEvent } = storeToRefs(useEventDetailsBaseStore())
	const { discountConfig } = storeToRefs(useEventDetailsRemoteDataStore())

	const doesEventTriggerCooldown = computed<boolean>(() => !sourceEvent.value || !!sourceEvent.value?.triggers_cooldown)

	const cooldownStartRaw = computed<DateTime | undefined>(() => {
		if (endDate.value?.isValid && doesEventTriggerCooldown.value) {
			return endDate.value
		}

		return undefined
	})

	const cooldownEndRaw = computed<DateTime | undefined>(() => {
		if (endDate.value?.isValid && isFinite(discountConfig.value.cooldownAfterLengthInDays) && doesEventTriggerCooldown.value) {
			return getEventEndDate(endDate.value, discountConfig.value.cooldownAfterLengthInDays)
		}

		return undefined
	})

	const cooldownStart = computed<string>(() => {
		if (cooldownStartRaw.value !== undefined) {
			return dateTimeFormattedShort(cooldownStartRaw.value.toUTC(), true)
		}

		return ''
	})

	const cooldownEnd = computed<string>(() => {
		if (cooldownEndRaw.value !== undefined) {
			return dateTimeFormattedShort(cooldownEndRaw.value.toUTC(), true)
		}

		return ''
	})

	const cooldownDuration = computed<Duration | null>(() => {
		if (!cooldownStartRaw.value || !cooldownEndRaw.value) {
			return null
		}

		return getDuration(cooldownStartRaw.value, cooldownEndRaw.value)
	})

	const isCooldownLengthValid = computed(() => isFinite(discountConfig.value.cooldownAfterLengthInDays))

	return {
		doesEventTriggerCooldown,
		isCooldownLengthValid,
		cooldownDuration,
		cooldownStart,
		cooldownStartRaw,
		cooldownEnd,
		cooldownEndRaw
	}
}
